import React, { useMemo, useState } from "react";
import { Button, Code, Dialog, Flex, Text, TextField } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { ActiveIntegrateCard, PermissionAlertDialog } from "components";
import { useDisconnectMcp, useGetMcpStatus } from "hooks";
import { useProfileContext } from "context";
import { useToast } from "../../../context/ToastContext";
import { CLAUDE_DISCONNECT_NOTE, ClaudeSetupDialog } from "./BrowseClaudeCard";

export function ActiveClaudeIntegration() {
  const { userProfile } = useProfileContext();
  const { data } = useGetMcpStatus(userProfile?.organisation_id);
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const disconnectMcp = useDisconnectMcp();
  const [setupOpen, setSetupOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [copied, setCopied] = useState(false);

  const isConnected = data?.data?.is_active === true;
  const isOwner = userProfile?.user_role === "Owner";
  const mcpUrl = data?.data?.mcp_url || "";
  const connectedBy = data?.data?.connected_by || "";
  const displayUrl = useMemo(
    () => mcpUrl || "Ask your admin to set MCP_PUBLIC_URL on the API.",
    [mcpUrl]
  );

  const handleDisconnectClick = () => {
    if (!isOwner) {
      setPermissionOpen(true);
      return;
    }
    setConfirmOpen(true);
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectMcp.mutateAsync({
        organisation_id: userProfile.organisation_id,
      });
      queryClient.invalidateQueries(["mcp-status", userProfile.organisation_id]);
      setConfirmOpen(false);
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message || "Could not disconnect Claude. Please try again."
      );
    } finally {
      setIsDisconnecting(false);
    }
  };

  const copyUrl = async () => {
    if (!mcpUrl) {
      setToastMessage("Connector URL is not configured yet.");
      return;
    }
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setToastMessage("Could not copy the URL. Select it and copy manually.");
    }
  };

  if (!isConnected) {
    return null;
  }

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Claude"
        subtitle={
          connectedBy
            ? `Ask read-only questions about your BConnect data · connected by ${connectedBy}`
            : "Ask read-only questions about your BConnect data"
        }
        image_url="claude-ai-logo.png"
        option={{
          text: "Disconnect",
          color: "red",
          onClick: handleDisconnectClick,
        }}
      >
        <ActiveIntegrateCard.Section
          name="Connector setup"
          subtitle="Copy the MCP URL and add it as a custom connector in Claude"
        >
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex gap="2" align="center" width="100%">
              <TextField.Root
                value={displayUrl}
                readOnly
                size="2"
                style={{ flex: 1, minWidth: 0 }}
              />
              <Button size="2" variant="soft" color="gray" onClick={copyUrl} disabled={!mcpUrl}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </Flex>
            <Text size="2" color="gray">
              In Claude, add a custom connector / MCP server and paste this URL
              (including the trailing <Code>/mcp/</Code>). Sign in on BConnect and approve
              one organisation.
            </Text>
            <Flex>
              <Button size="2" variant="soft" color="gray" onClick={() => setSetupOpen(true)}>
                View full setup
              </Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      {setupOpen ? (
        <ClaudeSetupDialog
          isOpen={setupOpen}
          onOpenChange={setSetupOpen}
          userProfile={userProfile}
          isConnected={isConnected}
          mcpUrl={mcpUrl}
          connectedBy={connectedBy}
        />
      ) : null}

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content maxWidth="440px">
          <Dialog.Title>Disconnect Claude?</Dialog.Title>
          <Flex direction="column" gap="3" mt="2">
            <Text size="2" color="gray">
              This revokes BConnect access for Claude for this organisation right away.
            </Text>
            <Text size="2" color="gray">
              {CLAUDE_DISCONNECT_NOTE}
            </Text>
            <Flex justify="end" gap="2">
              <Button
                size="2"
                variant="soft"
                color="gray"
                onClick={() => setConfirmOpen(false)}
                disabled={isDisconnecting}
              >
                Cancel
              </Button>
              <Button
                size="2"
                color="red"
                onClick={handleDisconnect}
                loading={isDisconnecting}
              >
                Disconnect
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={permissionOpen}
        onOpenChange={setPermissionOpen}
        message="Only owners can disconnect Claude"
      />
    </>
  );
}
